/**
 * What the client has actually done in the setup flow.
 *
 * A completed step is persisted by the setup API and mirrored in localStorage
 * so the panel can tick immediately while the request is in flight. Everything
 * the client *tells* us (their details, change notes, the domain they picked)
 * is still delivered by the panel itself — mail or an existing dashboard route
 * — so nothing is silently trapped here; this store only remembers that the
 * step is behind them.
 */

import axios from 'axios';
import type { SetupStatus, SetupTask } from './setupFlow';

const STORAGE_KEY = 'foundlio.setup.tasks.v1';

export type TaskId = 'confirm-details' | 'photos' | 'review-draft' | 'domain' | 'gbp';

/** Steps with a panel of their own inside /welcome. */
export const IN_FLOW_TASKS: readonly string[] = ['confirm-details', 'photos', 'review-draft', 'domain'];

export function isInFlowTask(taskId: string | null | undefined): boolean {
  return Boolean(taskId && IN_FLOW_TASKS.includes(taskId));
}

export interface TaskRecord {
  /** ISO timestamp of completion. Absent means "not done". */
  completedAt?: string;
  /** Free-form payload the panel captured — shown back as a receipt. */
  data?: Record<string, string>;
}

export type TaskStore = Partial<Record<string, TaskRecord>>;

function namespaceKey(demo: boolean): string {
  return demo ? `${STORAGE_KEY}.demo` : STORAGE_KEY;
}

export function readTaskStore(demo = false): TaskStore {
  try {
    const raw = window.localStorage.getItem(namespaceKey(demo));
    return raw ? (JSON.parse(raw) as TaskStore) : {};
  } catch {
    return {};
  }
}

export function saveTaskState(taskId: string, record: TaskRecord, demo = false): TaskStore {
  const next: TaskStore = { ...readTaskStore(demo) };
  if (record.completedAt) {
    next[taskId] = record;
  } else {
    delete next[taskId];
  }
  try {
    window.localStorage.setItem(namespaceKey(demo), JSON.stringify(next));
  } catch {
    // Private mode / quota — the tick is a convenience, never load-bearing.
  }
  window.dispatchEvent(new CustomEvent('foundlio:setup-tasks-changed'));

  if (!demo) {
    const request = record.completedAt
      ? axios.post(`/api/setup/tasks/${encodeURIComponent(taskId)}`, record)
      : axios.delete(`/api/setup/tasks/${encodeURIComponent(taskId)}`);

    request
      .then(() => window.dispatchEvent(new CustomEvent('foundlio:setup-tasks-saved')))
      .catch(() => {
        // The local tick is only optimistic; the next status fetch decides truth.
        window.dispatchEvent(new CustomEvent('foundlio:setup-tasks-save-failed'));
      });
  }

  return next;
}

export function clearTaskStore(demo = false): void {
  try {
    window.localStorage.removeItem(namespaceKey(demo));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('foundlio:setup-tasks-changed'));
}

/** Human label for the receipt line under a completed task. */
export function completionNote(record: TaskRecord | undefined): string | null {
  if (!record?.completedAt) return null;
  const when = new Date(record.completedAt);
  return `Done ${when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/**
 * Fold locally recorded completions over a derived status: tasks tick, the
 * checklist count moves, and each completion shows up in the activity feed so
 * the client sees their own actions in the same timeline as ours.
 */
export function applyTaskStore(status: SetupStatus, store: TaskStore): SetupStatus {
  const tasks: SetupTask[] = status.tasks.map((task) => {
    const record = store[task.id];
    if (!record?.completedAt) return task;
    return { ...task, status: 'done' };
  });

  const events = [...status.events];
  for (const [taskId, record] of Object.entries(store)) {
    if (!record?.completedAt) continue;
    const task = status.tasks.find((candidate) => candidate.id === taskId);
    if (!task) continue;
    // The server has already caught up with this one, so it is carrying its own
    // event — synthesising a second would show the client the same step twice.
    if (task.status === 'done') continue;
    events.push({
      id: `local-${taskId}`,
      at: record.completedAt,
      title: task.title,
      detail: record.data?.summary,
      actor: 'you',
    });
  }
  events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return { ...status, tasks, events };
}
